// @ts-nocheck
import React, { useState, useRef, useEffect, memo } from "react";
import { motion, useInView } from "framer-motion";
import { Trophy, Users, Calendar, Code, Award, Star } from "lucide-react";
import { BentoGrid, BentoGridItem } from "./ui/bento-grid.tsx";
import { cn } from "../lib/utils";
import "./SocietyHighlights.css";
import eventsHostedImage from "../assets/pastevents/WhatsApp Image 2025-08-25 at 22.42.57_f4f9cfe7.jpg";
import awardsWonImage from "../assets/pastevents/IMG_0995.JPG";
import projectsCompletedImage from "../assets/projects/ai-chatbot/cover.jpeg";
import activeMembersImage from "../assets/aiecgroup_cover.jpeg";

const SocietyHighlights = memo(() => {
    const [counters, setCounters] = useState({
        events: 0,
        projects: 0,
        members: 0,
        awards: 0,
    });

    const highlights = [
        {
            id: 1,
            title: "Industry Collaboration",
            description: "Partnered with leading AI startups for hands-on student projects",
            icon: Users,
            color: "#10B981",
        },
        {
            id: 2,
            title: "Research Publications",
            description: "10+ papers published by AIEC members in reputed journals/conferences",
            icon: Code,
            color: "#3B82F6",
        },
        {
            id: 3,
            title: "Techno Sapiens 2024",
            description: "Organized our flagship fest with 500+ participants across events",
            icon: Trophy,
            color: "#F59E0B",
        },
        {
            id: 4,
            title: "Innovative Solutions",
            description: "Developed real-world AI projects in healthcare, finance, and automation",
            icon: Star,
            color: "#8B5CF6",
        },
        {
            id: 5,
            title: "Community Impact",
            description: "Conducted 20+ workshops and outreach programs to spread AI awareness beyond campus",
            icon: Award,
            color: "#EC4899",
        },
        {
            id: 6,
            title: "Student Success",
            description: "AIEC members secured internships and placements at top tech companies",
            icon: Users,
            color: "#06B6D4",
        },
    ];

    // Duplicate highlights for seamless infinite loop (CSS handles the animation)
    const infiniteHighlights = [...highlights, ...highlights];

    const stats = [
        {
            id: 1,
            number: 50,
            label: "Events Hosted",
            icon: Calendar,
            suffix: "+",
            description: "Workshops, Seminars, & Hackathons",
            backgroundImage: eventsHostedImage,
        },
        {
            id: 2,
            number: 100,
            label: "Projects Completed",
            icon: Code,
            suffix: "+",
            description: "Innovative AI & ML Implementations",
            backgroundImage: projectsCompletedImage,
        },
        {
            id: 3,
            number: 150,
            label: "Active Members",
            icon: Users,
            suffix: "+",
            description: "Growing Community of Tech Enthusiasts",
            backgroundImage: activeMembersImage,
        },
        {
            id: 4,
            number: 100,
            label: "Awards Won",
            icon: Trophy,
            suffix: "+",
            description: "Recognition in National Competitions",
            backgroundImage: awardsWonImage,
        },
    ];

    const ref = React.useRef(null);
    const isInView = useInView(ref, { once: true, threshold: 0.3 });

    // Performance-optimized stat counters
    useEffect(() => {
        if (isInView) {
            const targetValues = {
                events: 50,
                projects: 100,
                members: 150,
                awards: 100,
            };

            const duration = 2000;
            const steps = 60;
            const stepDuration = duration / steps;

            let currentStep = 0;
            const interval = setInterval(() => {
                currentStep++;
                const progress = currentStep / steps;

                setCounters({
                    events: Math.floor(targetValues.events * progress),
                    projects: Math.floor(targetValues.projects * progress),
                    members: Math.floor(targetValues.members * progress),
                    awards: Math.floor(targetValues.awards * progress),
                });

                if (currentStep >= steps) {
                    clearInterval(interval);
                }
            }, stepDuration);

            return () => clearInterval(interval);
        }
    }, [isInView]);

    return (
        <section
            className="society-highlights relative"
            ref={ref}
        >
            <div className="container mx-auto px-4 py-16">
                <motion.div
                    className="section-header text-center mb-16"
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                >
                    <h2 className="text-4xl md:text-5xl font-bold mb-4 font-['Audiowide']">
                        Society <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">Highlights</span>
                    </h2>
                    <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                        Celebrating milestones, creating impact, and shaping the future of tech.
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    viewport={{ once: true }}
                    className="mb-20"
                >
                    <BentoGrid className="max-w-7xl mx-auto grid-cols-2 auto-rows-[13rem] md:auto-rows-[20rem] lg:grid-cols-4 md:grid-cols-2">
                        {stats.map((stat, i) => (
                            <BentoGridItem
                                key={stat.id}
                                title={
                                    <span className="text-base md:text-xl font-bold text-gray-200 block min-h-[2.5rem] md:min-h-fit">
                                        {stat.label}
                                    </span>
                                }
                                description={
                                    <span className="text-[10px] md:text-sm text-gray-400 block min-h-[1.5rem] md:min-h-[2.5rem] leading-tight">
                                        {stat.description}
                                    </span>
                                }
                                header={
                                    <div
                                        className={cn(
                                            "flex flex-1 w-full h-full min-h-[6rem] rounded-xl bg-gradient-to-br from-neutral-900 to-neutral-800 border border-neutral-700 items-center justify-center relative overflow-hidden group"
                                        )}
                                    >
                                        {stat.backgroundImage && (
                                            <>
                                                <div
                                                    className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat opacity-40"
                                                    style={{ backgroundImage: `url('${stat.backgroundImage}')` }}
                                                />
                                                <div className="absolute inset-0 z-[1] bg-black/35" />
                                            </>
                                        )}
                                        <div className="relative z-20 flex flex-col items-center">
                                            <stat.icon className="w-8 h-8 md:w-12 md:h-12 text-blue-400 mb-1 md:mb-2 group-hover:scale-110 transition-transform duration-300" />
                                            <span className="text-3xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-b from-white to-gray-500">
                                                {counters[Object.keys(counters)[i]]}{stat.suffix}
                                            </span>
                                        </div>
                                    </div>
                                }
                            />
                        ))}
                    </BentoGrid>
                </motion.div>

                <motion.div
                    className="highlights-section"
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    viewport={{ once: true }}
                >
                    <h3 className="text-3xl font-bold text-center mb-10 text-white">Recent Achievements</h3>

                    <div className="highlights-container relative w-full overflow-hidden">
                        <div className="highlights-scroll flex gap-8 py-4 no-scrollbar">
                            {infiniteHighlights.map((highlight, index) => (
                                <motion.div
                                    key={`highlight-${highlight.id}-${index}`}
                                    className="highlight-card glass min-w-[350px] md:min-w-[450px] p-6 rounded-2xl flex items-center gap-6 border border-white/10 bg-white/5 backdrop-blur-sm hover:bg-white/10 transition-colors"
                                    whileHover={{ scale: 1.02 }}
                                >
                                    <div
                                        className="highlight-icon w-12 h-12 rounded-full flex items-center justify-center border-2 shrink-0"
                                        style={{ borderColor: highlight.color, backgroundColor: `${highlight.color}20` }}
                                    >
                                        <highlight.icon className="w-6 h-6" style={{ color: highlight.color }} />
                                    </div>
                                    <div className="highlight-content">
                                        <h4 className="text-lg font-bold text-white mb-1">{highlight.title}</h4>
                                        <p className="text-sm text-gray-400">{highlight.description}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    className="highlights-cta text-center mt-16"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                    viewport={{ once: true }}
                >
                    <motion.button
                        className="px-8 py-3 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold text-lg hover:shadow-lg hover:shadow-blue-500/30 transition-all"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        Join Our Success Story
                    </motion.button>
                </motion.div>
            </div>
        </section>
    );
});

export default SocietyHighlights;
